import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { getToken } = useAdminAuth();
  
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d'); // 7d, 30d, 90d, all

  useEffect(() => {
    fetchStats();
  }, [timeRange]);

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/dashboard/stats?range=${timeRange}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-white text-xl">Cargando dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
          <p className="text-gray-400">Vista general del sistema</p>
        </div>
        
        {/* Time Range Selector */}
        <div className="flex gap-2">
          {['7d', '30d', '90d', 'all'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                timeRange === range
                  ? 'bg-[#D4AF37] text-black'
                  : 'bg-black/30 text-gray-400 hover:text-white border border-[#1a4d5c]/30'
              }`}
            >
              {range === 'all' ? 'Todo' : range.replace('d', ' días')}
            </button>
          ))}
        </div>
      </div>

      {/* Main KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Total Estudiantes"
          value={stats?.users?.total || 0}
          icon="👥"
          trend={stats?.users?.trend}
          color="blue"
          subtitle={`${stats?.users?.active || 0} activos`}
          onClick={() => navigate('/admin/users')}
        />
        
        <KPICard
          title="Revenue Total"
          value={`$${(stats?.revenue?.total || 0).toLocaleString()}`}
          icon="💰"
          trend={stats?.revenue?.trend}
          color="green"
          subtitle={`$${(stats?.revenue?.monthly || 0).toLocaleString()} este mes`}
          onClick={() => navigate('/admin/payments')}
        />
        
        <KPICard
          title="Tasa Conversión"
          value={`${stats?.conversion?.rate || 0}%`}
          icon="📈"
          trend={stats?.conversion?.trend}
          color="purple"
          subtitle={`${stats?.conversion?.preview_to_active || 0} activados`}
        />
        
        <KPICard
          title="Asistencia Promedio"
          value={`${stats?.attendance?.average || 0}%`}
          icon="✅"
          trend={stats?.attendance?.trend}
          color="gold"
          subtitle={`${stats?.attendance?.total_sessions || 0} sesiones`}
          onClick={() => navigate('/admin/sessions')}
        />
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatusCard
          title="Preview"
          count={stats?.users?.by_status?.preview || 0}
          icon="👁️"
          color="yellow"
          percentage={(stats?.users?.by_status?.preview || 0) / (stats?.users?.total || 1) * 100}
        />
        <StatusCard
          title="Activos"
          count={stats?.users?.by_status?.active || 0}
          icon="🚀"
          color="green"
          percentage={(stats?.users?.by_status?.active || 0) / (stats?.users?.total || 1) * 100}
        />
        <StatusCard
          title="Suspendidos"
          count={stats?.users?.by_status?.suspended || 0}
          icon="⚠️"
          color="red"
          percentage={(stats?.users?.by_status?.suspended || 0) / (stats?.users?.total || 1) * 100}
        />
      </div>

      {/* Alerts & Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alerts */}
        <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
          <h3 className="text-lg font-bold text-white mb-4">🚨 Alertas</h3>
          <div className="space-y-3">
            {stats?.alerts?.failed_payments > 0 && (
              <Alert
                type="error"
                message={`${stats.alerts.failed_payments} pago(s) fallido(s)`}
                action="Ver"
                onClick={() => navigate('/admin/payments')}
              />
            )}
            {stats?.alerts?.pending_projects > 0 && (
              <Alert
                type="warning"
                message={`${stats.alerts.pending_projects} proyecto(s) pendiente(s) de revisión`}
                action="Revisar"
                onClick={() => navigate('/admin/users')}
              />
            )}
            {stats?.alerts?.open_tickets > 0 && (
              <Alert
                type="info"
                message={`${stats.alerts.open_tickets} ticket(s) abierto(s)`}
                action="Ver"
                onClick={() => navigate('/admin/users')}
              />
            )}
            {stats?.alerts?.low_attendance > 0 && (
              <Alert
                type="warning"
                message={`${stats.alerts.low_attendance} estudiante(s) con baja asistencia (<50%)`}
                action="Ver"
                onClick={() => navigate('/admin/users')}
              />
            )}
            {(!stats?.alerts || Object.values(stats.alerts).every(v => v === 0)) && (
              <div className="text-center py-8 text-gray-400">
                ✅ No hay alertas pendientes
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
          <h3 className="text-lg font-bold text-white mb-4">⚡ Acciones Rápidas</h3>
          <div className="grid grid-cols-2 gap-3">
            <QuickAction
              icon="👤"
              label="Ver Usuarios"
              onClick={() => navigate('/admin/users')}
            />
            <QuickAction
              icon="🎓"
              label="Becas"
              onClick={() => navigate('/admin/scholarships')}
            />
            <QuickAction
              icon="💳"
              label="Pagos"
              onClick={() => navigate('/admin/payments')}
            />
            <QuickAction
              icon="🎥"
              label="Sesiones"
              onClick={() => navigate('/admin/sessions')}
            />
            <QuickAction
              icon="🔔"
              label="Webhooks"
              onClick={() => navigate('/admin/webhooks')}
            />
            <QuickAction
              icon="📋"
              label="Auditoría"
              onClick={() => navigate('/admin/audit')}
            />
          </div>
        </div>
      </div>

      {/* Scholarships & Revenue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scholarship Stats */}
        <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
          <h3 className="text-lg font-bold text-white mb-4">🎓 Becas Otorgadas</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Total Becados:</span>
              <span className="text-white font-bold text-xl">{stats?.scholarships?.total_students || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Descuento Total:</span>
              <span className="text-[#D4AF37] font-bold text-xl">
                ${(stats?.scholarships?.total_discount || 0).toLocaleString()}
              </span>
            </div>
            <div className="space-y-2 pt-4 border-t border-[#1a4d5c]/30">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Beca 100%:</span>
                <span className="text-white">{stats?.scholarships?.by_percentage?.['100'] || 0} estudiantes</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Beca 80-99%:</span>
                <span className="text-white">{stats?.scholarships?.by_percentage?.['80-99'] || 0} estudiantes</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Beca 50-79%:</span>
                <span className="text-white">{stats?.scholarships?.by_percentage?.['50-79'] || 0} estudiantes</span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Performers */}
        <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
          <h3 className="text-lg font-bold text-white mb-4">🏆 Top Performers</h3>
          <div className="space-y-3">
            {stats?.top_performers?.length > 0 ? (
              stats.top_performers.slice(0, 5).map((student, index) => (
                <div
                  key={student.id}
                  onClick={() => navigate(`/admin/users/${student.id}`)}
                  className="flex items-center justify-between p-3 bg-black/30 rounded-lg hover:bg-[#1a4d5c]/20 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '⭐'}</div>
                    <div>
                      <div className="text-white font-medium">{student.name}</div>
                      <div className="text-xs text-gray-400">{student.cohort}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#D4AF37] font-bold">{student.attendance}%</div>
                    <div className="text-xs text-gray-400">asistencia</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-400">
                No hay datos disponibles
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
        <h3 className="text-lg font-bold text-white mb-4">📊 Actividad Reciente</h3>
        <div className="space-y-2">
          {stats?.recent_activity?.length > 0 ? (
            stats.recent_activity.slice(0, 10).map((activity, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-[#1a4d5c]/20 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{getActivityIcon(activity.type)}</span>
                  <div>
                    <span className="text-white text-sm">{activity.description}</span>
                    <span className="text-gray-500 text-xs ml-2">{activity.user}</span>
                  </div>
                </div>
                <div className="text-gray-400 text-xs">
                  {new Date(activity.timestamp).toLocaleTimeString('es-ES', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-400">
              No hay actividad reciente
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// KPI Card Component
function KPICard({ title, value, icon, trend, color, subtitle, onClick }) {
  const colorClasses = {
    gold: 'from-[#D4AF37] to-[#F4E4B7]',
    blue: 'from-[#1a4d5c] to-[#2a6d7c]',
    green: 'from-green-600 to-emerald-600',
    purple: 'from-purple-600 to-pink-600',
    red: 'from-red-600 to-rose-600',
  };

  return (
    <div
      onClick={onClick}
      className={`bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30 hover:border-[#D4AF37]/50 transition-all ${
        onClick ? 'cursor-pointer' : ''
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center text-2xl`}>
          {icon}
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-sm font-medium ${
            trend > 0 ? 'text-green-400' : trend < 0 ? 'text-red-400' : 'text-gray-400'
          }`}>
            {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'} {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div className="text-gray-400 text-sm mb-1">{title}</div>
      <div className="text-white text-3xl font-bold mb-2">{value}</div>
      {subtitle && <div className="text-gray-500 text-xs">{subtitle}</div>}
    </div>
  );
}

// Status Card Component
function StatusCard({ title, count, icon, color, percentage }) {
  const colorClasses = {
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    red: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  return (
    <div className={`rounded-lg p-6 border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-3xl">{icon}</span>
        <span className="text-4xl font-bold">{count}</span>
      </div>
      <div className="text-sm font-medium mb-2">{title}</div>
      <div className="w-full bg-black/30 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${color === 'yellow' ? 'bg-yellow-400' : color === 'green' ? 'bg-green-400' : 'bg-red-400'}`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
      <div className="text-xs mt-2 opacity-70">{percentage.toFixed(1)}% del total</div>
    </div>
  );
}

// Alert Component
function Alert({ type, message, action, onClick }) {
  const typeClasses = {
    error: 'bg-red-500/10 border-red-500/30 text-red-400',
    warning: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
    info: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
  };

  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${typeClasses[type]}`}>
      <span className="text-sm">{message}</span>
      {onClick && (
        <button
          onClick={onClick}
          className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded text-xs font-medium transition-colors"
        >
          {action}
        </button>
      )}
    </div>
  );
}

// Quick Action Component
function QuickAction({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-center p-4 bg-black/30 hover:bg-[#1a4d5c]/30 rounded-lg transition-colors border border-[#1a4d5c]/30"
    >
      <span className="text-3xl mb-2">{icon}</span>
      <span className="text-white text-sm font-medium">{label}</span>
    </button>
  );
}

// Helper function
function getActivityIcon(type) {
  const icons = {
    user_created: '👤',
    payment_completed: '💳',
    scholarship_applied: '🎓',
    session_attended: '✅',
    project_submitted: '📝',
    ticket_created: '🎫',
    user_activated: '🚀',
    user_suspended: '⚠️',
  };
  return icons[type] || '📌';
}
